/** @format */
import { DOCUMENT } from '@angular/core';
import { UIView } from '@uirouter/angular';
import { Transition } from '@uirouter/core';
import { firstValueFrom } from 'rxjs';
import { injectHook, SisStateDeclaration } from 'sis-components';
import { StudentAttainmentsComponent } from './attainments/student-attainments/student-attainments.component';
import { BasicInfoComponent } from './basic-info/basic-info.component';
import { ProfileEnrolmentsComponent } from './enrolments/profile-enrolments.component';
import { GrantsPageComponent } from './payments-and-grants/grants/grants-page/grants-page.component';
import { PaymentAndGrantTabsComponent } from './payments-and-grants/payment-and-grant-tabs/payment-and-grant-tabs.component';
import { PaymentsComponent } from './payments-and-grants/payments/payments.component';
import { StudentPersonGroupsComponent } from './personGroups/student-person-groups/student-person-groups.component';
import { ProfileSecondaryNavigationComponent } from './profile-secondary-navigation/profile-secondary-navigation.component';
import { ProfileShellComponent } from './profile-shell/profile-shell.component';
import { StudentProfileService } from './student-profile.service';
import { StudyRightsComponent } from './studyRights/study-rights/study-rights.component';

const profileRootState = 'student.logged-in.profile';
const applicationsRootState = 'student.logged-in.profile.applications';
const paymentsAndGrantsRootState = 'student.logged-in.profile.payments-and-grants';
const basicInfoRootState = 'student.logged-in.profile.basic-info';
const personGroupsRootState = 'student.logged-in.profile.person-groups';
const enrolmentsRootState = 'student.logged-in.profile.enrolments';
const attainmentsRootState = 'student.logged-in.profile.attainments';

// The profile tabs are declared on each state that should show them rather than filled by the parent shell.
// A native-Angular child cannot clear a native-Angular parent's named view with an empty `{}` override, so the
// secondaryNavigation slot is owned by the leaf states that want it (the tabs + applications.list) and left
// unfilled everywhere else (application detail/create/workflow views).
export const profileSecondaryNavigationView = {
    [`secondaryNavigation@${profileRootState}`]: { component: ProfileSecondaryNavigationComponent },
};

export const profileStates: SisStateDeclaration[] = [
    {
        // Migrated from AngularJS: profile.route.js (state 'student.logged-in.profile')
        name: profileRootState,
        url: '/profile',
        views: {
            '': { component: ProfileShellComponent },
        },
        resolve: {
            profile: studentProfileResolver,
        },
        data: {
            showHeader: true,
            showHome: true,
            hideLastNode: false,
            browserTabTitle: 'APPLICATION_STUDENT_PROFILE',
        },
        headerParams: {
            skipBreadcrumb: false,
            displayNameKey: 'MY_INFORMATION',
        },
        redirectTo: 'student.logged-in.profile.study-right',
    },
    {
        // Migrated from AngularJS: profile.route.js (state 'student.logged-in.profile.applications')
        name: applicationsRootState,
        url: '/applications',
        component: UIView,
        headerParams: {
            displayNameKey: 'PROFILE.TAB_APPLICATIONS',
        },
        redirectTo: 'student.logged-in.profile.applications.list',
        onEnter: injectHook(transition => transition.injector().get(DOCUMENT)?.defaultView?.scrollTo(0, 0)),
    },
    {
        name: 'student.logged-in.profile.study-right',
        url: '/study-rights',
        views: {
            '': { component: StudyRightsComponent },
            ...profileSecondaryNavigationView,
        },
        headerParams: {
            skipBreadcrumb: false,
            displayNameKey: 'PROFILE.STUDY-RIGHTS',
            displayPageTitleNameKey: 'MY_INFORMATION',
        },
    },
    {
        name: attainmentsRootState,
        url: '/attainments',
        views: {
            '': { component: StudentAttainmentsComponent },
            ...profileSecondaryNavigationView,
        },
        headerParams: {
            skipBreadcrumb: false,
            displayNameKey: 'PROFILE.ATTAINMENTS',
            displayPageTitleNameKey: 'MY_INFORMATION',
        },
        resolve: {
            studentProfile: studentProfileResolver,
        },
    },
    {
        name: paymentsAndGrantsRootState,
        url: '/payments-and-grants',
        views: {
            '': { component: PaymentAndGrantTabsComponent },
            ...profileSecondaryNavigationView,
        },
        redirectTo: `${paymentsAndGrantsRootState}.payments`,
        headerParams: {
            skipBreadcrumb: false,
            displayNameKey: 'PROFILE.PAYMENTS_AND_GRANTS.TAB_TITLE',
            displayPageTitleNameKey: 'MY_INFORMATION',
        },
        resolve: {
            studentProfile: studentProfileResolver,
        },
    },
    {
        name: basicInfoRootState,
        url: '/basic-info',
        views: {
            '': { component: BasicInfoComponent },
            ...profileSecondaryNavigationView,
        },
        headerParams: {
            skipBreadcrumb: false,
            displayNameKey: 'PROFILE.PERSONAL_INFO',
            displayPageTitleNameKey: 'MY_INFORMATION',
        },
        resolve: {
            studentProfile: studentProfileResolver,
        },
    },
    {
        name: `${paymentsAndGrantsRootState}.payments`,
        url: '/payments',
        component: PaymentsComponent,
        headerParams: {
            skipBreadcrumb: false,
            displayNameKey: 'PROFILE.PAYMENTS.TAB_TITLE',
            displayPageTitleNameKey: 'MY_INFORMATION',
        },
        resolve: {
            studentProfile: studentProfileResolver,
        },
    },
    {
        name: `${paymentsAndGrantsRootState}.grants`,
        url: '/grants',
        component: GrantsPageComponent,
        headerParams: {
            skipBreadcrumb: false,
            displayNameKey: 'PROFILE.GRANTS.TAB_TITLE',
            displayPageTitleNameKey: 'MY_INFORMATION',
        },
        resolve: {
            studentProfile: studentProfileResolver,
        },
    },
    {
        name: personGroupsRootState,
        url: '/person-groups',
        views: {
            '': { component: StudentPersonGroupsComponent },
            ...profileSecondaryNavigationView,
        },
        headerParams: {
            skipBreadcrumb: false,
            displayNameKey: 'PROFILE.PERSON_GROUPS.TITLE',
            displayPageTitleNameKey: 'MY_INFORMATION',
        },
    },
    {
        name: `${enrolmentsRootState}`,
        url: '/enrolments',
        views: {
            '': { component: ProfileEnrolmentsComponent },
            ...profileSecondaryNavigationView,
        },
        headerParams: {
            skipBreadcrumb: false,
            displayNameKey: 'PROFILE.ENROLMENTS',
            displayPageTitleNameKey: 'MY_INFORMATION',
        },
        resolve: {},
    },
];

export function studentProfileResolver($transition$: Transition): Promise<any> {
    return firstValueFrom($transition$.injector().get(StudentProfileService).profile());
}

studentProfileResolver.$inject = ['$transition$'];
